import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, Search, ChevronRight, Rocket, LayoutDashboard, Compass,
  Code, Globe, Crown, HelpCircle, Keyboard, Lightbulb, ExternalLink,
  Zap, ArrowRight, Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from '@/lib/utils';
import { tools, categories, categoryDescriptions, toolWalkthroughs, type ToolCategory } from '@/lib/tools-config';

const sections = [
  { id: 'getting-started', label: 'Getting Started', icon: Rocket, color: 'from-blue-500/20 to-cyan-500/20' },
  { id: 'tool-categories', label: 'Tool Categories', icon: Compass, color: 'from-violet-500/20 to-purple-500/20' },
  { id: 'walkthroughs', label: 'Walkthroughs', icon: Lightbulb, color: 'from-amber-500/20 to-yellow-500/20' },
  { id: 'code-studio', label: 'Code Studio', icon: Code, color: 'from-emerald-500/20 to-green-500/20' },
  { id: 'my-sites', label: 'My Sites', icon: Globe, color: 'from-sky-500/20 to-blue-500/20' },
  { id: 'pro-tools', label: 'Pro Tools', icon: Crown, color: 'from-amber-500/20 to-orange-500/20' },
  { id: 'faq', label: 'FAQs', icon: HelpCircle, color: 'from-rose-500/20 to-pink-500/20' },
  { id: 'shortcuts', label: 'Shortcuts', icon: Keyboard, color: 'from-indigo-500/20 to-blue-500/20' },
];

const faqs = [
  { q: 'Is my data private?', a: 'Yes! Most tools run entirely in your browser. No files are uploaded to any server unless explicitly stated (e.g., AI tools that call external APIs).' },
  { q: 'Are there usage limits?', a: 'Most tools are unlimited. AI-powered tools and Temp Mail use credits. Free users start with a base credit allowance.' },
  { q: 'How do I get more credits?', a: 'Credits can be added by an admin. Premium plans include higher credit allocations.' },
  { q: 'Can I use TAM Store offline?', a: 'Client-side tools (PDF, Image, Text tools) work offline once loaded. AI tools and Temp Mail require an internet connection.' },
  { q: 'How do I publish a site from Code Studio?', a: 'Open Code Studio, build your project, then click the "Publish" button. Your site gets a unique URL under tam27.store/s/your-slug.' },
  { q: 'What file formats are supported?', a: 'PDF tools support .pdf files. Image tools support .png, .jpg, .webp, .gif. Office to PDF supports .docx, .xlsx, .pptx.' },
  { q: 'Is there a file size limit?', a: 'Most tools handle files up to 50MB. Larger files may cause slower processing in the browser.' },
  { q: 'How do I delete my account?', a: 'Contact support through the Settings page. Account deletion removes all associated data.' },
];

const shortcuts = [
  { keys: 'Ctrl + K', description: 'Inline AI edit in Code Studio (edit selected code with natural language)' },
  { keys: '@ + filename', description: 'Reference a file in TAM AI chat for context-aware suggestions' },
  { keys: 'Ctrl + S', description: 'Save current file in Code Studio' },
  { keys: 'Ctrl + Shift + P', description: 'Open command palette in Code Studio editor' },
];

const Guide = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('getting-started');
  const contentRef = useRef<HTMLDivElement>(null);

  const toolsByCategory = useMemo(() => {
    const map: Partial<Record<ToolCategory, typeof tools>> = {};
    for (const tool of tools) {
      if (!map[tool.category]) map[tool.category] = [];
      map[tool.category]!.push(tool);
    }
    return map;
  }, []);

  const walkthroughEntries = useMemo(() => {
    return Object.entries(toolWalkthroughs).map(([path, wt]) => ({
      path,
      ...wt,
    }));
  }, []);

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;
    const q = searchQuery.toLowerCase();
    return sections.filter(s => {
      if (s.label.toLowerCase().includes(q)) return true;
      if (s.id === 'tool-categories') {
        return tools.some(t => t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
      }
      if (s.id === 'faq') {
        return faqs.some(f => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
      }
      if (s.id === 'walkthroughs') {
        return walkthroughEntries.some(w => w.title.toLowerCase().includes(q));
      }
      return false;
    });
  }, [searchQuery, walkthroughEntries]);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sectionEls = sections.map(s => document.getElementById(`guide-${s.id}`));
      for (let i = sectionEls.length - 1; i >= 0; i--) {
        const el = sectionEls[i];
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const el = document.getElementById(`guide-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="animate-fade-in max-w-6xl mx-auto">
      {/* Hero Header */}
      <div className="relative mb-8 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-primary/10 via-accent/5 to-transparent border border-border/50 overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative flex items-start gap-4">
          <div className="p-3 rounded-xl bg-primary/20 border border-primary/30 shadow-lg shadow-primary/10">
            <BookOpen className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">User Guide</h1>
            <p className="text-muted-foreground">Everything you need to know about TAM Store — tools, features, tips & more.</p>
          </div>
        </div>

        {/* Quick stats */}
        <div className="flex flex-wrap gap-3 mt-5">
          <Badge variant="outline" className="gap-1.5 py-1 px-3 bg-background/50 backdrop-blur-sm">
            <Zap className="h-3 w-3 text-primary" /> {tools.length}+ Tools
          </Badge>
          <Badge variant="outline" className="gap-1.5 py-1 px-3 bg-background/50 backdrop-blur-sm">
            <Compass className="h-3 w-3 text-primary" /> {categories.length - 1} Categories
          </Badge>
          <Badge variant="outline" className="gap-1.5 py-1 px-3 bg-background/50 backdrop-blur-sm">
            <Lightbulb className="h-3 w-3 text-primary" /> {walkthroughEntries.length} Walkthroughs
          </Badge>
        </div>
      </div>

      <div className="flex gap-8">
        {/* Table of Contents - Sticky sidebar */}
        <aside className="hidden lg:block w-52 shrink-0">
          <div className="sticky top-20 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-sm bg-accent/20 border-border/50 focus:bg-accent/40 transition-colors"
              />
            </div>

            <nav className="space-y-0.5">
              {filteredSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={cn(
                    "flex items-center gap-2.5 w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 group",
                    activeSection === section.id
                      ? "bg-primary/15 text-primary font-semibold border-l-2 border-primary shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
                  )}
                >
                  <section.icon className={cn(
                    "h-4 w-4 shrink-0 transition-colors",
                    activeSection === section.id ? "text-primary" : "group-hover:text-primary"
                  )} />
                  <span className="truncate">{section.label}</span>
                </button>
              ))}
            </nav>

            {/* Quick link to restart tour */}
            <div className="pt-3 border-t border-border/30">
              <p className="text-[11px] text-muted-foreground/60 mb-2 px-2">Need help?</p>
              <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-primary hover:bg-accent/40 transition-all">
                <Sparkles className="h-3.5 w-3.5" />
                Back to Dashboard
              </Link>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div ref={contentRef} className="flex-1 space-y-6 min-w-0 pb-12">

          {/* Mobile search */}
          <div className="lg:hidden relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search guide..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-accent/20 border-border/50"
            />
          </div>

          {/* Getting Started */}
          <GuideSection id="getting-started" title="Getting Started" icon={Rocket}>
            <div className="grid sm:grid-cols-2 gap-4">
              <InfoCard
                title="Sign Up & Login"
                description="Create an account with your email or sign in with Google. You'll be taken to the Dashboard immediately."
                emoji="🔐"
              />
              <InfoCard
                title="Dashboard Overview"
                description="See platform stats, recently used tools, favorites, and announcements. Pin tools for quick access."
                emoji="📊"
              />
              <InfoCard
                title="Navigation"
                description='Use the sidebar to browse categories or click "All Tools" to see everything. Each category is expandable.'
                emoji="🧭"
              />
              <InfoCard
                title="Finding Tools"
                description="Use the search bar at the top to quickly find any tool by name or browse by category."
                emoji="🔍"
              />
            </div>
          </GuideSection>

          {/* Tool Categories */}
          <GuideSection id="tool-categories" title="Tool Categories" icon={Compass}>
            <Accordion type="multiple" className="space-y-2">
              {categories.filter(c => c.id !== 'all').map((cat) => {
                const catTools = toolsByCategory[cat.id as ToolCategory] || [];
                const catDesc = categoryDescriptions[cat.id as ToolCategory] || '';
                if (catTools.length === 0) return null;

                return (
                  <AccordionItem key={cat.id} value={cat.id} className="border border-border/40 rounded-xl overflow-hidden bg-accent/5">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent/30 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 rounded-lg bg-primary/10">
                          <cat.icon className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-semibold text-sm">{cat.label}</span>
                        <Badge variant="outline" className="text-[10px] px-2 py-0 bg-background/50">{catTools.length}</Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      {catDesc && <p className="text-sm text-muted-foreground mb-3 italic">{catDesc}</p>}
                      <div className="grid sm:grid-cols-2 gap-1.5">
                        {catTools.map((tool) => (
                          <Link
                            key={tool.path}
                            to={tool.path}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-accent/40 transition-all group border border-transparent hover:border-border/40"
                          >
                            <div className="p-1 rounded-md bg-primary/10 group-hover:bg-primary/20 transition-colors">
                              <tool.icon className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <span className="text-sm font-medium flex-1 truncate">{tool.name}</span>
                            {tool.isNew && <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[8px] px-1 py-0 h-3.5">NEW</Badge>}
                            <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </Link>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </GuideSection>

          {/* Popular Tools Walkthrough */}
          <GuideSection id="walkthroughs" title="Popular Tools Walkthrough" icon={Lightbulb}>
            <div className="grid sm:grid-cols-2 gap-3">
              {walkthroughEntries.map((wt) => (
                <Card key={wt.path} className="border-border/40 bg-accent/5 hover:bg-accent/10 transition-colors group">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-sm group-hover:text-primary transition-colors">{wt.title}</h4>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">{wt.steps.length} steps</Badge>
                    </div>
                    <ol className="space-y-1.5 mb-3">
                      {wt.steps.slice(0, 3).map((step, i) => (
                        <li key={i} className="flex gap-2.5 text-xs">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                          <span className="text-muted-foreground pt-0.5 line-clamp-1">{step}</span>
                        </li>
                      ))}
                      {wt.steps.length > 3 && (
                        <li className="text-xs text-muted-foreground/60 pl-7">+{wt.steps.length - 3} more steps...</li>
                      )}
                    </ol>
                    {wt.tips && wt.tips.length > 0 && (
                      <div className="p-2 rounded-lg bg-primary/5 border border-primary/10 mb-3">
                        <p className="text-[10px] font-semibold text-primary mb-1">💡 Tips</p>
                        <ul className="space-y-0.5">
                          {wt.tips.map((tip, i) => (
                            <li key={i} className="text-[11px] text-muted-foreground">• {tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <Link to={wt.path}>
                      <Button variant="outline" size="sm" className="w-full gap-1.5 h-8 text-xs group-hover:border-primary/40 group-hover:text-primary transition-colors">
                        Open Tool <ChevronRight className="h-3 w-3" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </GuideSection>

          {/* Code Studio */}
          <GuideSection id="code-studio" title="Code Studio" icon={Code}>
            <div className="grid sm:grid-cols-2 gap-4">
              <InfoCard title="Layout" description='3-column layout: File Explorer (left), Code Editor (center), and Right Panel with Live Preview and TAM AI tabs.' emoji="📐" />
              <InfoCard title="Files Panel" description="Create, rename, and delete files. Supports HTML, CSS, JavaScript, and text files." emoji="📁" />
              <InfoCard title="Monaco Editor" description="Full-featured editor (same as VS Code) with syntax highlighting, auto-completion, and error detection." emoji="⌨️" />
              <InfoCard title="Live Preview" description="See changes in real-time. The preview updates automatically as you type." emoji="👁️" />
            </div>
            <div className="mt-4 space-y-3">
              <FeatureHighlight
                title="TAM AI Assistant"
                description='Ask questions, get suggestions, or request code edits. Use @filename to provide file context.'
                kbd={null}
              />
              <FeatureHighlight
                title="Inline Edit"
                description="Select code and press the shortcut to rewrite it with natural language instructions."
                kbd="Ctrl+K"
              />
              <FeatureHighlight
                title="One-Click Publish"
                description='Click "Publish" to deploy your project and get a unique URL under tam27.store.'
                kbd={null}
              />
            </div>
          </GuideSection>

          {/* My Sites & Publishing */}
          <GuideSection id="my-sites" title="My Sites & Publishing" icon={Globe}>
            <div className="grid sm:grid-cols-3 gap-4">
              <InfoCard title="Publish" description="After building in Code Studio, click Publish. Choose a slug and your site goes live instantly." emoji="🚀" />
              <InfoCard title="Manage" description="View all published sites from My Sites. See URLs, view counts, and publish dates." emoji="⚙️" />
              <InfoCard title="Analytics" description="Track visitors, page views, devices, browsers, and referrers for each published site." emoji="📈" />
            </div>
          </GuideSection>

          {/* Pro Tools & Catalogue */}
          <GuideSection id="pro-tools" title="Pro Tools & Catalogue" icon={Crown}>
            <div className="grid sm:grid-cols-2 gap-4">
              <InfoCard title="Pro Tools" description="BIN Generator, Card Validator, and BIN Lookup — available to premium users for advanced card utilities." emoji="👑" />
              <InfoCard title="Catalogue" description="Browse curated digital products and resources managed by platform admins." emoji="🛍️" />
            </div>
          </GuideSection>

          {/* FAQs */}
          <GuideSection id="faq" title="Frequently Asked Questions" icon={HelpCircle}>
            <Accordion type="single" collapsible className="space-y-2">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border/40 rounded-xl overflow-hidden bg-accent/5">
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-accent/30 text-sm font-medium text-left">
                    <div className="flex items-center gap-2.5">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">?</span>
                      {faq.q}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 text-sm text-muted-foreground pl-[52px]">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </GuideSection>

          {/* Tips & Shortcuts */}
          <GuideSection id="shortcuts" title="Tips & Shortcuts" icon={Keyboard}>
            <div className="space-y-2 mb-6">
              {shortcuts.map((sc, i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-xl bg-accent/10 border border-border/30 hover:bg-accent/20 transition-colors">
                  <kbd className="px-3 py-1.5 rounded-lg bg-background border border-border text-xs font-mono font-bold whitespace-nowrap shadow-sm min-w-[120px] text-center">{sc.keys}</kbd>
                  <span className="text-sm text-muted-foreground">{sc.description}</span>
                </div>
              ))}
            </div>
            <div className="p-5 rounded-xl bg-gradient-to-br from-primary/10 via-accent/5 to-transparent border border-primary/20">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-primary" />
                Best Practices
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {[
                  'Star frequently used tools for quick access from the Dashboard.',
                  'Use the search bar to find tools faster than browsing categories.',
                  'In Code Studio, use TAM AI for complex code generation and debugging.',
                  'PDF tools work offline — no internet required for merging, splitting, or editing.',
                  'Check "New Tools" on the Dashboard regularly for the latest additions.',
                ].map((tip, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="text-primary mt-0.5">✦</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </GuideSection>
        </div>
      </div>
    </div>
  );
};

/* ─── Sub-components ─── */

const GuideSection = ({ id, title, icon: Icon, children }: { id: string; title: string; icon: React.ElementType; children: React.ReactNode }) => (
  <section id={`guide-${id}`} className="scroll-mt-20">
    <div className="rounded-2xl border border-border/40 overflow-hidden bg-gradient-to-b from-accent/5 to-transparent">
      <div className="px-5 py-4 border-b border-border/30 bg-gradient-to-r from-primary/8 to-transparent">
        <h2 className="text-lg font-bold flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-primary/15">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          {title}
        </h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </section>
);

const InfoCard = ({ title, description, emoji }: { title: string; description: string; emoji: string }) => (
  <div className="p-4 rounded-xl border border-border/30 bg-accent/5 hover:bg-accent/15 transition-all duration-200 group">
    <div className="flex items-start gap-3">
      <span className="text-xl">{emoji}</span>
      <div>
        <h4 className="font-semibold text-sm mb-1 group-hover:text-primary transition-colors">{title}</h4>
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

const FeatureHighlight = ({ title, description, kbd: kbdKey }: { title: string; description: string; kbd: string | null }) => (
  <div className="flex items-start gap-3 p-3 rounded-xl bg-primary/5 border border-primary/15">
    <Zap className="h-4 w-4 text-primary shrink-0 mt-0.5" />
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-0.5">
        <h4 className="text-sm font-semibold">{title}</h4>
        {kbdKey && <kbd className="px-1.5 py-0.5 rounded bg-accent text-[10px] font-mono border border-border/50">{kbdKey}</kbd>}
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  </div>
);

export default Guide;
